import React from "react";
import {
  useMacroCollections,
  ProgramIterationProvider,
} from "../MacroCollectionContext";

type MacroProgramsRepeaterProps = {
  /** Slot for the content to repeat for each program - renders as a function component */
  item?: React.FC;
  /** Whether to show when there are no programs */
  showWhenEmpty?: boolean;
  puck?: { dragRef?: React.Ref<any> };
};

/**
 * Repeater block that iterates over all program bookings in the reservation.
 *
 * Inside the repeater, use bindings like:
 * - {{program.Name}} - Program name
 * - {{program.Days}} - Number of days
 * - {{program.StartDate}} - Start date
 * - {{program.EndDate}} - End date
 * - {{program.Price}} - Formatted price
 * - {{program.Quantity}} - Quantity (for grouped programs)
 * - {{program.ordinal}} - 1, 2, 3...
 */
const MacroProgramsRepeaterBlock = {
  label: "Programs Repeater",
  llmDescription: `A repeater block that iterates over all program bookings in the reservation.

IMPORTANT - SLOT DATA MODEL:
This block uses the Puck SLOT pattern (NOT DropZones). The 'item' field is a SLOT that stores
child component data INLINE within the props, NOT in a separate zones object.

Correct PuckData structure for ProgramsRepeater:
{
  "type": "ProgramsRepeater",
  "props": {
    "id": "programs-repeater",
    "item": [
      { "type": "Html", "props": { "id": "html-1", "content": "<p>{{program.Name}}</p>" } },
      { "type": "Divider", "props": { "id": "div-1" } }
    ]
  }
}

WRONG (do NOT use zones):
{
  "type": "ProgramsRepeater",
  "props": { "id": "programs-repeater" }
}
// with separate zones["programs-repeater:item"] - THIS IS WRONG!

Inside this repeater, you can use program-specific bindings:
  - {{program.Name}} - Program name (e.g., "Weight Loss Program")
  - {{program.Days}} - Number of days (integer)
  - {{program.DaysUnit}} - "day" or "days" based on Days value
  - {{program.StartDate}} - Formatted start date
  - {{program.EndDate}} - Formatted end date
  - {{program.Price}} - Formatted price WITH currency (e.g., "฿35,000") - DO NOT add ฿ before this!
  - {{program.Quantity}} - Quantity for grouped programs
  - {{program.ordinal}} - Position number (1, 2, 3...)
  - {{program.count}} - Total number of programs

CRITICAL: {{program.Price}} already includes ฿ symbol. Writing "฿{{program.Price}}" will produce "฿฿35,000".`,
  inline: true,
  fields: {
    item: { type: "slot" as const },
    showWhenEmpty: {
      type: "radio" as const,
      label: "Show when no programs",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
  },
  defaultProps: {
    item: [] as any[],
    showWhenEmpty: false,
  },
  render: (props: MacroProgramsRepeaterProps) => {
    const { item: Items, showWhenEmpty = false, puck } = props;
    const collections = useMacroCollections();
    const programs = collections.programs;

    if (!showWhenEmpty && programs.length === 0) {
      return null;
    }

    const ref = puck?.dragRef as any;

    return (
      <div ref={ref} style={{ display: "contents" }}>
        {programs.map((program, index) => (
          <ProgramIterationProvider
            key={index}
            program={program}
            index={index}
            count={programs.length}
          >
            {Items && <Items />}
            {program.ClosureNote && <p>{program.ClosureNote}</p>}
          </ProgramIterationProvider>
        ))}
      </div>
    );
  },
};

export default MacroProgramsRepeaterBlock;
